import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Spinner, StatCard } from "@/components/common";
import { ExecutiveReport } from "@/components/reports/ExecutiveReport";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#00F0FF", "#38BDF8", "#34D399", "#FBBF24", "#F87171", "#A78BFA"];

export default function Reports() {
  const [tab, setTab] = useState("Fleet Analytics");
  const [d, setD] = useState(null);
  const [sla, setSla] = useState(null);

  useEffect(() => { api.get("/reports/summary").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (tab === "SLA Compliance") api.get("/sla-report").then((r) => setSla(r.data)).catch(() => {}); }, [tab]);

  if (!d) return <div className="p-6"><Spinner /></div>;
  return (
    <div className="p-6 fade-up">
      <PageHeader title="Reports" subtitle="Fleet analytics & customer-facing executive reports" testid="reports-page" />
      <div className="flex gap-1 border-b border-slate-800 mb-4">
        {["Fleet Analytics", "Executive Report", "SLA Compliance"].map((t) => <button key={t} data-testid={`report-tab-${t.toLowerCase().replace(/ /g, "-")}`} onClick={() => setTab(t)}
          className={"px-3 py-2 text-[13px] font-medium border-b-2 -mb-px " + (tab === t ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200")}>{t}</button>)}
      </div>

      {tab === "Fleet Analytics" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card-surface rounded-lg p-4">
            <h3 className="font-heading font-bold text-[14px] text-slate-200 mb-3">OS Distribution</h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart><Pie data={d.os_distribution} dataKey="count" nameKey="os" cx="50%" cy="50%" outerRadius={90} label={(e) => e.os}>
                {d.os_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie>
                <Tooltip contentStyle={{ background: "#121824", border: "1px solid #1E293B", fontSize: 12 }} /></PieChart>
            </ResponsiveContainer>
          </div>
          <div className="card-surface rounded-lg p-4">
            <h3 className="font-heading font-bold text-[14px] text-slate-200 mb-3">Agent Versions</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.agent_versions}><XAxis dataKey="version" tick={{ fill: "#64748B", fontSize: 11 }} /><YAxis tick={{ fill: "#64748B", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#121824", border: "1px solid #1E293B", fontSize: 12 }} cursor={{ fill: "rgba(0,240,255,0.05)" }} />
                <Bar dataKey="count" fill="#00F0FF" radius={[4, 4, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {tab === "Executive Report" && <ExecutiveReport />}

      {tab === "SLA Compliance" && (!sla ? <Spinner /> : <SlaCompliance sla={sla} />)}
    </div>
  );
}

function SlaCompliance({ sla }) {
  const P = ["P1", "P2", "P3", "P4"];
  const o = sla.overall;
  const tone = (p) => (p >= 90 ? "text-emerald-300" : p >= 70 ? "text-amber-300" : "text-rose-300");
  return (<div className="space-y-4" data-testid="sla-compliance">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <StatCard label="Response SLA Met" value={o.response_pct + "%"} sub={`${o.response_met}/${o.response_total} tickets`} tone={o.response_pct >= 90 ? "emerald" : "amber"} />
      <StatCard label="Resolution SLA Met" value={o.resolution_pct + "%"} sub={`${o.resolution_met}/${o.resolution_total} resolved`} tone={o.resolution_pct >= 90 ? "emerald" : "amber"} />
    </div>
    <div className="card-surface rounded-lg overflow-hidden"><table className="w-full text-left text-[13px]" data-testid="sla-by-priority">
      <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80 border-b border-slate-800"><th className="px-3 py-2">Priority</th><th className="px-3 py-2">Response met</th><th className="px-3 py-2">Resolution met</th></tr></thead>
      <tbody className="divide-y divide-slate-800/60">{P.map((p) => { const b = sla.by_priority[p]; return <tr key={p}><td className="px-3 py-2 font-mono text-cyan-300">{p}</td><td className={"px-3 py-2 " + tone(b.response_pct)}>{b.response_met}/{b.response_total} ({b.response_pct}%)</td><td className={"px-3 py-2 " + tone(b.resolution_pct)}>{b.resolution_met}/{b.resolution_total} ({b.resolution_pct}%)</td></tr>; })}</tbody>
    </table></div>
    {sla.by_customer?.length > 0 && <div className="card-surface rounded-lg overflow-hidden"><table className="w-full text-left text-[13px]">
      <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80 border-b border-slate-800"><th className="px-3 py-2">Customer</th><th className="px-3 py-2">Response</th><th className="px-3 py-2">Resolution</th></tr></thead>
      <tbody className="divide-y divide-slate-800/60">{sla.by_customer.map((c) => <tr key={c.customer_id}><td className="px-3 py-2 text-slate-200">{c.customer}</td><td className={"px-3 py-2 " + tone(c.response_pct)}>{c.response_pct}%</td><td className={"px-3 py-2 " + tone(c.resolution_pct)}>{c.resolution_pct}%</td></tr>)}</tbody>
    </table></div>}
  </div>);
}
